import { RefObject, useCallback, useEffect, useState } from "react";

export interface CalculatorScrollEdges {
  /** True when the mobile calculator panel is scrolled to the top. */
  isScrolledToTop: boolean;
  /** True when the mobile calculator panel is scrolled to the bottom. */
  isScrolledToBottom: boolean;
  /** Removes top shadow on the fixed mobile banner when flush with scroll top. */
  mobileBannerClassName: string;
  /** Removes bottom shadow on fixed price bars when flush with scroll bottom. */
  priceContainerClassName: string;
}

function getScrollEdges(element: HTMLElement) {
  const isScrolledToTop = element.scrollTop === 0;
  const isScrolledToBottom = element.offsetHeight + element.scrollTop >= element.scrollHeight;

  return { isScrolledToTop, isScrolledToBottom };
}

/**
 * Tracks scroll position inside the mobile calculator overlay (`floating-wrapper`).
 * Used to toggle shadow classes on the sticky header and footer price bars so they
 * blend seamlessly when the user reaches the top or bottom of the scrollable area.
 */
export function useCalculatorScrollEdges(
  scrollContainerRef: RefObject<HTMLElement | null>
): CalculatorScrollEdges {
  const [isScrolledToTop, setIsScrolledToTop] = useState(true);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);

  const handleScroll = useCallback((event: Event) => {
    const element = event.currentTarget as HTMLElement;
    const { isScrolledToTop: atTop, isScrolledToBottom: atBottom } = getScrollEdges(element);

    setIsScrolledToTop((prev) => (prev === atTop ? prev : atTop));
    setIsScrolledToBottom((prev) => (prev === atBottom ? prev : atBottom));
  }, []);

  useEffect(() => {
    const element = scrollContainerRef.current;
    if (!element) {
      return;
    }

    const initialEdges = getScrollEdges(element);
    setIsScrolledToTop(initialEdges.isScrolledToTop);
    setIsScrolledToBottom(initialEdges.isScrolledToBottom);

    element.addEventListener("scroll", handleScroll);

    return () => {
      element.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll, scrollContainerRef]);

  return {
    isScrolledToTop,
    isScrolledToBottom,
    mobileBannerClassName: isScrolledToTop ? "scroll-top" : "",
    priceContainerClassName: isScrolledToBottom ? "scroll-bottom" : ""
  };
}
